import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Space,
  Table,
  Input,
  Select,
  Radio,
  Form,
  AutoComplete,
} from 'antd';
import {
  RequeueEmailButton,
  CustomNotification,
  WithLoading,
} from 'common/components';
import { apiHandler } from 'utils/api';
import {
  getQaSpecCorrectiveActions,
  saveQaSpecCorrectiveAction,
  sendRequeueEmail,
} from 'services/qaSpec';

import { useCheckPermissions } from 'hooks';
import { PERMISSION_EDIT_QA_SPEC_CARS } from 'pages/qa-spec/hooks';

import EditContainerCARs from '../container/EditContainerCARs';
import { responsibilityList } from 'pages/new-evaluation-form/constants/selectList';

import './QaCorrectiveAction.less';

const { Option } = Select;

const QaCorrectiveAction = ({ productId, isTabActive }) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialCompletionDates, setInitialCompletionDates] = useState({});

  // Fetch corrective actions data when the component is mounted or productId changes
  useEffect(() => {
    if (isTabActive && productId) {
      const fetchCorrectiveActions = async () => {
        setLoading(true);
        await apiHandler({
          service: getQaSpecCorrectiveActions,
          params: { productId },
          successCallback: (responseData) => {
            if (responseData?.length > 0) {
              const formattedData = responseData.flatMap((item) =>
                item.actionTypes.map((action, index) => ({
                  key: `${item.productId}-${index}-${action.carRelatedTo}`,
                  correctiveAction: action.correctiveAction,
                  carRelatedTo: action.carRelatedTo,
                  status: action.status || '',
                  completionDate: action.completionDate
                    ? new Date(action.completionDate).toISOString().slice(0, 10)
                    : '',
                  comments: action.comments || '',
                  approveReject: action.approveReject || '',
                  responsibility: action.responsibility || '',
                  notes: action.notes || '',
                }))
              );

              setRawData(responseData);
              setData(formattedData);

              // Track initial completion dates from API data
              const initialDates = {};
              formattedData.forEach((item, index) => {
                initialDates[index] = item.completionDate || '';
              });
              setInitialCompletionDates(initialDates);

              form.setFieldsValue({ actions: formattedData });
            } else {
              setData([]);
              setRawData([]);
              setInitialCompletionDates({});
            }
          },
          failCallback: (error) => {
            console.error('Error fetching data:', error);
            setError([]);
          },
          onFinally: () => setLoading(false),
        });
      };

      fetchCorrectiveActions();
    }
  }, [productId, isTabActive, form]);

  const handleSave = async (toggleEdit) => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const cumulativeLengths = rawData.reduce((acc, subarray) => {
        const currentLength = subarray.actionTypes.length;
        const previousCumulativeLength =
          acc.length > 0 ? acc[acc.length - 1] : 0;
        acc.push(previousCumulativeLength + currentLength);
        return acc;
      }, []);

      cumulativeLengths.unshift(0);
      const updatedPayload = rawData.map((item, headerIndex) => ({
        ...item,
        actionTypes: item.actionTypes.map((action, actionIndex) => {
          const actionFormValues =
            values.actions[cumulativeLengths[headerIndex] + actionIndex] || {};

          const completionDate =
            actionFormValues.approveReject === 'Approve'
              ? new Date().toISOString() // Set today's date for Approve
              : actionFormValues.approveReject === 'Reject'
              ? null // Clear date for Reject
              : initialCompletionDates[
                  cumulativeLengths[headerIndex] + actionIndex
                ] || null;

          return {
            ...action,
            approveReject:
              actionFormValues.approveReject || action.approveReject,
            completionDate,
            comments: actionFormValues.comments || '',
            status: actionFormValues.status || null,
            responsibility: actionFormValues.responsibility || '',
          };
        }),
      }));

      await apiHandler({
        service: saveQaSpecCorrectiveAction,
        params: updatedPayload,
        successMessage: 'Successfully saved Corrective Actions!',
        errorMessage: 'Failed to save Corrective Actions!',
        successCallback: () => {
          setData(
            updatedPayload.flatMap((item) =>
              item.actionTypes.map((action, index) => ({
                key: `${item.productId}-${index}-${action.carRelatedTo}`,
                ...action,
                isEdit: false,
              }))
            )
          );
        },
        onFinally: () => {
          setLoading(false);
          toggleEdit();
        },
      });
    } catch (error) {
      setLoading(false);
      CustomNotification.error('Please resolve errors before saving.');
    }
  };

  const handleRequeueEmail = async () => {
    await apiHandler({
      service: sendRequeueEmail,
      params: { productId },
      successMessage: 'Requeue Email sent successfully!',
      errorMessage: 'Failed to send Requeue Email!',
    });
  };

  const handleCancel = () => {
    // Reset form fields and completionDate to the initial state
    form.resetFields();

    // Reset the data to initial values to reflect the correct state
    const updatedData = data.map((item, index) => {
      return {
        ...item,
        completionDate: item.completionDate, // Reset to initial fetched date
      };
    });

    setData(updatedData);
  };

  const handleApproveRejectChange = (index, value) => {
    const today = new Date().toISOString().slice(0, 10);
    const updatedCompletionDates = { ...initialCompletionDates };
    updatedCompletionDates[index] = value === 'Approve' ? today : '';
    setInitialCompletionDates(updatedCompletionDates);

    const updatedFields = form.getFieldsValue().actions;
    updatedFields[index] = {
      ...updatedFields[index],
      approveReject: value,
      completionDate: updatedCompletionDates[index],
    };

    form.setFieldsValue({
      actions: updatedFields,
    });
  };

  const columns = [
    {
      title: 'Corrective Action Related To',
      dataIndex: 'carRelatedTo',
      key: 'carRelatedTo',
      width: '200px',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: '150px',
    },
    {
      title: 'Corrective Actions',
      dataIndex: 'correctiveAction',
      key: 'correctiveAction',
      width: '200px',
    },
    {
      title: 'Responsibility',
      dataIndex: 'responsibility',
      key: 'responsibility',
      width: '250px',
      render: (_, record, index) => (
        <Form.Item
          name={['actions', index, 'responsibility']}
          initialValue={record.responsibility}
        >
          <AutoComplete
            disabled={!record.isEdit}
            options={responsibilityList}
            filterOption={(inputValue, option) => {
              return (
                option?.label
                  ?.toLowerCase()
                  ?.indexOf(inputValue?.toLowerCase()) > -1
              );
            }}
            getPopupContainer={(triggerNode) => {
              return triggerNode;
            }}
          >
            <Input />
          </AutoComplete>
        </Form.Item>
      ),
    },
    {
      title: 'Approve/Reject',
      dataIndex: 'approveReject',
      key: 'approveReject',
      width: '200px',
      render: (_, record, index) => (
        <Form.Item
          name={['actions', index, 'approveReject']}
          initialValue={record.approveReject}
        >
          <Radio.Group
            disabled={!record.isEdit}
            onChange={(e) => handleApproveRejectChange(index, e.target.value)}
          >
            <Radio value='Approve'>Approve</Radio>
            <Radio value='Reject'>Reject</Radio>
          </Radio.Group>
        </Form.Item>
      ),
    },
    {
      title: 'Completion Date',
      dataIndex: 'completionDate',
      key: 'completionDate',
      width: '150px',
      render: (_, record, index) => (
        <Form.Item
          name={['actions', index, 'completionDate']}
          initialValue={record.completionDate}
        >
          <Input
            type='date'
            value={record.completionDate || ''}
            disabled={true}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      render: (_, record, index) => (
        <Form.Item
          name={['actions', index, 'comments']}
          initialValue={record.comments}
        >
          <Input.TextArea rows={4} disabled={!record.isEdit} />
        </Form.Item>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '150px',
      render: (_, record, index) => (
        <Form.Item
          name={['actions', index, 'status']}
          initialValue={record.status}
        >
          <Select disabled={!record.isEdit}>
            <Option value='In Progress'>In Progress</Option>
            <Option value='Completed'>Completed</Option>
            <Option value='Not Applicable'>Not Applicable</Option>
          </Select>
        </Form.Item>
      ),
    },
  ];

  const hasEditCARsPermission = useCheckPermissions(
    PERMISSION_EDIT_QA_SPEC_CARS
  );

  return (
    <Form form={form} layout='vertical'>
      <EditContainerCARs
        productId={productId}
        handleSave={handleSave}
        handleCancel={handleCancel}
        float
      >
        {(edit) => (
          <WithLoading loading={loading}>
            <Row style={{ marginBottom: 12 }} justify='end'>
              <Col>
                <Space direction='vertical' align='end'>
                  <RequeueEmailButton
                    text='Requeue Email'
                    onClick={handleRequeueEmail}
                    disabled={!hasEditCARsPermission}
                  />
                </Space>
              </Col>
            </Row>
            <Table
              dataSource={data.map((item) => ({ ...item, isEdit: edit }))}
              columns={columns}
              pagination={false}
              rowKey='key'
              className={`corrective-actions-table ${
                edit ? 'table--edit' : ''
              }`}
            />
          </WithLoading>
        )}
      </EditContainerCARs>
    </Form>
  );
};

export default QaCorrectiveAction;
